import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import type { UsageProfileRequest, UsageProfileResponse } from '@rheora/enrollment-contract';

import { funnelQueryKeys } from './funnel.queries';
import { postUsageProfile } from './usage-profile.fetchers';

/**
 * Record the shopper's chosen home size on the funnel session
 * (QA-2026-08-05 BLOCKER 1). Components call THIS, never the fetcher.
 *
 * ── WHY A MUTATION AND NOT A QUERY ─────────────────────────────────────────
 * It is a POST with a server-side side effect (the session write) and an
 * idempotency key. It is fired once, when the shopper commits their consumption
 * choice — not on every radio click, which would spend the session's request
 * budget on a preference.
 *
 * The funnel snapshot is invalidated on success because the session it
 * summarises has just changed; a later `GET /bff/estimate` must not be answered
 * from a snapshot taken before the size was recorded.
 *
 * ⚠️ A FAILURE HERE MUST NOT BLOCK SHOPPING, and it does not corrupt the number
 * the customer sees. If the write never lands, the BFF applies its documented
 * medium-home default — to BOTH the estimate screen and the deposit, because
 * both read the same session. The shopper may be estimated as a typical home
 * rather than their own, but they are never shown one bill and charged a deposit
 * derived from another. That equality is a property of the server wiring, not of
 * this call succeeding.
 */
export function useRecordUsageProfileMutation(): UseMutationResult<
  UsageProfileResponse,
  Error,
  UsageProfileRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postUsageProfile,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: funnelQueryKeys.snapshot() }),
  });
}
